import { lazy, Suspense, type ComponentProps } from "react";
import { useAppSelector } from "@/redux/hooks";
import { memo, useCallback } from "react";
import { authSelectors } from "@/redux/slices/auth.slice";
import { useOracaoPermission } from "@/hooks/useOracaoPermission";
import { useEnsinoPermission } from "@/hooks/useEnsinoPermission";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import type { PermissaoExigida } from "@/lib/acesso-negado";
import { hasOrgTipoPermissao } from "@/lib/igreja-permissoes";

// #705: este arquivo entra no bundle inicial (Routes); a tela de acesso negado (e o AuthLayout
// que ela puxa) só baixa quando for mostrada, fora do HTML público.
const NaoAutorizadoLazy = lazy(() => import("@/pages/NaoAutorizado"));
export function NaoAutorizado(props: ComponentProps<typeof NaoAutorizadoLazy>) {
  return (
    <Suspense fallback={null}>
      <NaoAutorizadoLazy {...props} />
    </Suspense>
  );
}

export const FeatureFlag = memo(function FeatureFlag({
  flag,
  children,
}: {
  flag: string;
  children?: React.ReactNode;
}) {
  const flagActive = useFeatureFlag(flag);
  const showFeatureFlags = useAppSelector(
    (state) => state.generalReducer.showFeatureFlags,
  );

  if (!flag) {
    // Se a flag não for fornecida, apenas renderize as crianças
    return <>{children}</>;
  }

  if (flagActive) {
    // Se a flag estiver ativa, renderize as crianças
    return <>{children}</>;
  }

  if (showFeatureFlags) {
    return <div className="bg-red-500 ring-red-500 ring-4">{children}</div>;
  }

  // Se a flag não estiver ativa, não renderize nada
  return null;
});

export const PequenosGruposFeatureFlag = memo(function PequenosGruposFeatureFlag({
  children,
}: {
  children?: React.ReactNode;
}) {
  const Login = useAppSelector(authSelectors.Login);
  const showFeatureFlags = useAppSelector(
    (state) => state.generalReducer.showFeatureFlags,
  );

  const isPequenosGruposEnabled = useFeatureFlag("PequenosGrupos");

  // Verificar se o login tem PequenosGrupos habilitado
  const isLoginPequenosGruposEnabled = Login?.PequenosGrupos === true;

  // Verificar se ambas as condições estão satisfeitas
  const isPequenosGruposAvailable = isPequenosGruposEnabled && isLoginPequenosGruposEnabled;

  if (isPequenosGruposAvailable) {
    // Se ambas as condições estiverem satisfeitas, renderize as crianças
    return <>{children}</>;
  }

  if (showFeatureFlags) {
    return <div className="bg-red-500 ring-red-500 ring-4">{children}</div>;
  }

  // Se as condições não estiverem satisfeitas, não renderize nada
  return null;
});

export const EcommerceFeatureFlag = memo(function EcommerceFeatureFlag({
  children,
}: {
  children?: React.ReactNode;
}) {
  const showFeatureFlags = useAppSelector(
    (state) => state.generalReducer.showFeatureFlags,
  );

  const isEcommerceEnabled = useFeatureFlag("Ecommerce");

  if (isEcommerceEnabled) {
    return <>{children}</>;
  }

  if (showFeatureFlags) {
    return <div className="bg-red-500 ring-red-500 ring-4">{children}</div>;
  }

  return null;
});

export const EnsinoFeatureFlag = memo(function EnsinoFeatureFlag({
  children,
}: {
  children?: React.ReactNode;
}) {
  const Login = useAppSelector(authSelectors.Login);
  const showFeatureFlags = useAppSelector(
    (state) => state.generalReducer.showFeatureFlags,
  );

  const isEnsinoEnabled = useFeatureFlag("Ensino");
  const user = useAppSelector((state) => state.authReducer);

  // Verificar se o login tem Ensino habilitado
  const isLoginEnsinoEnabled = Login?.Ensino === true;

  // Verificar se ambas as condições estão satisfeitas
  const isEnsinoAvailable =
    isEnsinoEnabled && (isLoginEnsinoEnabled || user?.Master === true);

  if (isEnsinoAvailable) {
    // Se ambas as condições estiverem satisfeitas, renderize as crianças
    return <>{children}</>;
  }

  if (showFeatureFlags) {
    return <div className="bg-red-500 ring-red-500 ring-4">{children}</div>;
  }

  // Se as condições não estiverem satisfeitas, não renderize nada
  return null;
});

export const InfantilFeatureFlag = memo(function InfantilFeatureFlag({
  children,
}: {
  children?: React.ReactNode;
}) {
  const Login = useAppSelector(authSelectors.Login);
  const showFeatureFlags = useAppSelector(
    (state) => state.generalReducer.showFeatureFlags,
  );

  const isInfantilEnabled = useFeatureFlag("Infantil");
  const isLoginInfantilEnabled = Login?.Infantil === true;
  const isInfantilAvailable = isInfantilEnabled && isLoginInfantilEnabled;

  if (isInfantilAvailable) {
    return <>{children}</>;
  }

  if (showFeatureFlags) {
    return <div className="bg-red-500 ring-red-500 ring-4">{children}</div>;
  }

  return null;
});

/** EAD no ensino (player, modalidade, dúvidas). Flag Unleash: EnsinoEad */
export const EnsinoEadFeatureFlag = memo(function EnsinoEadFeatureFlag({
  children,
}: {
  children?: React.ReactNode;
}) {
  const isEnabled = useFeatureFlag("EnsinoEad");
  const showFeatureFlags = useAppSelector(
    (state) => state.generalReducer.showFeatureFlags,
  );

  if (isEnabled) {
    return <>{children}</>;
  }

  if (showFeatureFlags) {
    return <div className="bg-red-500 ring-red-500 ring-4">{children}</div>;
  }

  return null;
});

export const SiteDaIgrejaFeatureFlag = memo(function SiteDaIgrejaFeatureFlag({
  children,
}: {
  children?: React.ReactNode;
}) {
  const Login = useAppSelector(authSelectors.Login);
  const showFeatureFlags = useAppSelector(
    (state) => state.generalReducer.showFeatureFlags,
  );

  const isSiteDaIgrejaEnabled = useFeatureFlag("SiteDaIgreja");
  const isLoginSiteEnabled = Login?.Site === true;
  const isAvailable = isSiteDaIgrejaEnabled && isLoginSiteEnabled;

  if (isAvailable) {
    return <>{children}</>;
  }

  if (showFeatureFlags) {
    return <div className="bg-red-500 ring-red-500 ring-4">{children}</div>;
  }

  return null;
});

const RECURSO_POR_FLAG: Record<string, string> = {
  ListagemParticipantes: "Participantes",
  Padrinho: "Padrinhos",
  CheckoutTransparente: "Cupons",
  Ensino: "Ensino",
  SiteDaIgreja: "Site da igreja",
  AnalyticsIA: "Indicadores",
};

const PERMISSAO_ORACAO: PermissaoExigida = { escopo: "organizacao", codigos: ["ORACAO"] };
export const PERMISSAO_ENSINO: PermissaoExigida = { escopo: "organizacao", codigos: ["ENSINO"] };

export const FeatureRoute = memo(function FeatureRoute({
  flag,
  children,
}: {
  flag: string;
  children?: React.ReactNode;
}) {
  const isEnabled = useFeatureFlag(flag);

  const renderContent = useCallback(() => {
    if (isEnabled) {
      return <>{children}</>;
    }
    return <NaoAutorizado motivo="recurso-desligado" recurso={RECURSO_POR_FLAG[flag]} />;
  }, [isEnabled, children, flag]);

  return renderContent();
});

export const OracaoPermissionRoute = memo(function OracaoPermissionRoute({
  children,
}: {
  children?: React.ReactNode;
}) {
  const hasPermission = useOracaoPermission();

  const renderContent = useCallback(() => {
    if (hasPermission) {
      return <>{children}</>;
    }
    return <NaoAutorizado motivo="sem-permissao" permissao={PERMISSAO_ORACAO} tela="Pedidos de oração" />;
  }, [hasPermission, children]);

  return renderContent();
});

const PERMISSAO_INFANTIL: PermissaoExigida = { escopo: "organizacao", codigos: ["INFANTIL"] };

/**
 * Auditoria item 5: o backend (assertInfantilModuleAccess) exige modulo ligado na organizacao e
 * permissao INFANTIL (ou Master). Sem isso as telas do infantil abriam vazias com 403 nas chamadas.
 */
export const InfantilPermissionRoute = memo(function InfantilPermissionRoute({
  children,
  tela,
}: {
  children?: React.ReactNode;
  tela?: string;
}) {
  const user = useAppSelector((state) => state.authReducer);
  const selectedOrg = useAppSelector(authSelectors.Login);
  const moduloLigado = Boolean(selectedOrg?.Infantil);
  const hasPermission = hasOrgTipoPermissao(user, selectedOrg?.Id, "INFANTIL");

  if (!moduloLigado) {
    return <NaoAutorizado motivo="recurso-desligado" recurso="Ministério Infantil" />;
  }
  if (!hasPermission) {
    return <NaoAutorizado motivo="sem-permissao" permissao={PERMISSAO_INFANTIL} tela={tela ?? "Ministério Infantil"} />;
  }
  return <>{children}</>;
});

export const EnsinoPermissionRoute = memo(function EnsinoPermissionRoute({
  children,
}: {
  children?: React.ReactNode;
}) {
  const hasPermission = useEnsinoPermission();

  const renderContent = useCallback(() => {
    if (hasPermission) {
      return <>{children}</>;
    }
    return <NaoAutorizado motivo="sem-permissao" permissao={PERMISSAO_ENSINO} />;
  }, [hasPermission, children]);

  return renderContent();
});
